import { z } from 'zod/v3';
import {
  ControlEvidenceStatusEnumSchema,
  type VaultDocumentCategory,
  type VaultDocumentSource,
  type VaultDocumentStatus,
  VaultDocumentCategorySchema,
  VaultDocumentSourceSchema,
  VaultDocumentStatusSchema,
} from '../generated/enums';

import { PolicyReviewRecordSchema, RecordPolicyReviewSchema } from './policy-review.dto';

export const DocumentReviewReminderDetailsSchema = z.object({
  entityKey: z.string(),
  tests: z.array(z.object({ id: z.string(), title: z.string() })),
});

export const RecordVaultDocumentReviewSchema = RecordPolicyReviewSchema.omit({
  policyDocumentId: true, policyDocumentVersionId: true,
}).extend({ documentId: z.string().min(1), documentVersionId: z.string().min(1) }).strict();
export type RecordVaultDocumentReviewInput = z.infer<typeof RecordVaultDocumentReviewSchema>;

export const VaultDocumentReviewRecordSchema = PolicyReviewRecordSchema.omit({ policyDocumentVersionId: true }).extend({
  documentVersionId: z.string(),
});
export type VaultDocumentReviewRecordDTO = z.infer<typeof VaultDocumentReviewRecordSchema>;

export interface VaultDocumentReviewHistoryDTO {
  title: string;
  reviewTarget: { versionId: string; version: number } | null;
  canReview: boolean;
  records: VaultDocumentReviewRecordDTO[];
  targets: Array<{ id: string; title: string; nextRetestAt: string | null }>;
}
// ========== Vault Documents DTOs (RFC-144) ==========

// Re-export the auto-generated Prisma enum schemas under the *Enum names that
// callers already use. Single source of truth is `schema.prisma` → regenerated
// by `pnpm --filter server db:generate`. The previous hand-maintained
// z.enum([...]) here silently drifted from the Prisma enum (e.g. issue #476's
// CERTIFICATE category broke uploadFile validation until the duplicate was
// updated by hand) — don't reintroduce parallel declarations.
export {
  VaultDocumentCategorySchema as VaultDocumentCategoryEnum,
  VaultDocumentSourceSchema as VaultDocumentSourceEnum,
  VaultDocumentStatusSchema as VaultDocumentStatusEnum,
} from '../generated/enums';
export type {
  VaultDocumentCategory,
  VaultDocumentSource,
  VaultDocumentStatus,
} from '../generated/enums';

export const VaultLockedTestStatusEnum = ControlEvidenceStatusEnumSchema;

export const VaultDocLinkedTestDTOSchema = z.object({
  statusId: z.string(),
  title: z.string(),
  taskCode: z.string().nullable(),
  status: ControlEvidenceStatusEnumSchema,
  linkedAt: z.coerce.date(),
  versionId: z.string().nullable(),
  documentVersion: z.number().nullable(),
});
export type VaultDocLinkedTestDTO = z.infer<typeof VaultDocLinkedTestDTOSchema>;

export const VaultDocSuggestedTestDTOSchema = z.object({
  statusId: z.string(),
  title: z.string(),
  taskCode: z.string().nullable(),
  confidence: z.enum(['high', 'medium', 'low']),
  reasoning: z.string().nullable(),
});
export type VaultDocSuggestedTestDTO = z.infer<typeof VaultDocSuggestedTestDTOSchema>;

export const VaultDocLockedByTestDTOSchema = z.object({
  statusId: z.string(),
  title: z.string(),
  status: VaultLockedTestStatusEnum,
});
export type VaultDocLockedByTestDTO = z.infer<typeof VaultDocLockedByTestDTOSchema>;

export const VaultDocumentDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  programId: z.string(),
  auditCycleId: z.string().nullable(),
  title: z.string(),
  category: VaultDocumentCategorySchema,
  description: z.string().nullable(),
  content: z.string().nullable(),
  tiptapJson: z.unknown().nullable(),
  fileKey: z.string().nullable(),
  fileName: z.string().nullable(),
  fileMimeType: z.string().nullable(),
  fileSize: z.number().nullable(),
  sourceType: VaultDocumentSourceSchema,
  sourceChatId: z.string().nullable(),
  sourceMessageId: z.string().nullable(),
  controlRefs: z.array(z.string()),
  tags: z.array(z.string()),
  status: VaultDocumentStatusSchema,
  effectiveFrom: z.coerce.date().nullable(),
  effectiveTo: z.coerce.date().nullable(),
  reviewedAt: z.coerce.date().nullable(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  keyDocumentKey: z.string().nullable().optional(),
  preparedVersionId: z.string().nullable().optional(),
  preparedAt: z.coerce.date().nullable().optional(),
  preparedBy: z.string().nullable().optional(),
  contentHash: z.string().optional(),
  associatedControls: z.array(z.object({ framework: z.string(), ref: z.string() })).default([]),
  // Joined control def IDs
  controlDefIds: z.array(z.string()).optional(),
  // Lock state (RFC-style audit-integrity guard — locked when at least one
  // linked CES is SUBMITTED or APPROVED, mirroring evidenceDocLink.unlink)
  isLocked: z.boolean().default(false),
  lockedByTests: z.array(VaultDocLockedByTestDTOSchema).default([]),
  linkedTests: z.array(VaultDocLinkedTestDTOSchema).default([]),
  evidenceSuggestionSummary: z
    .object({
      suggestedCount: z.number(),
      confirmedCount: z.number(),
      rejectedCount: z.number(),
    })
    .optional(),
  suggestedTests: z
    .array(
      z.object({
        statusId: z.string(),
        taskCode: z.string().nullable(),
        title: z.string().nullable(),
      }),
    )
    .default([]),
});
export type VaultDocumentDTO = z.infer<typeof VaultDocumentDTOSchema>;

export const CreateVaultDocumentDTOSchema = z.object({
  title: z.string().min(1).max(500),
  category: VaultDocumentCategorySchema,
  description: z.string().nullable().optional(),
  content: z.string().nullable().optional(),
  tiptapJson: z.unknown().nullable().optional(),
  sourceType: VaultDocumentSourceSchema.optional(),
  sourceChatId: z.string().nullable().optional(),
  sourceMessageId: z.string().nullable().optional(),
  controlRefs: z.array(z.string()).optional(),
  controlDefIds: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  status: VaultDocumentStatusSchema.optional(),
  auditCycleId: z.string().nullable().optional(),
  effectiveFrom: z.coerce.date().nullable().optional(),
  effectiveTo: z.coerce.date().nullable().optional(),
});
export type CreateVaultDocumentDTO = z.infer<typeof CreateVaultDocumentDTOSchema>;

export const UpdateVaultDocumentDTOSchema = z.object({
  title: z.string().min(1).max(500).optional(),
  category: VaultDocumentCategorySchema.optional(),
  description: z.string().nullable().optional(),
  content: z.string().nullable().optional(),
  tiptapJson: z.unknown().nullable().optional(),
  controlRefs: z.array(z.string()).optional(),
  controlDefIds: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  status: VaultDocumentStatusSchema.optional(),
  auditCycleId: z.string().nullable().optional(),
  effectiveFrom: z.coerce.date().nullable().optional(),
  effectiveTo: z.coerce.date().nullable().optional(),
});
export type UpdateVaultDocumentDTO = z.infer<typeof UpdateVaultDocumentDTOSchema>;

export const VaultDocumentListFilterSchema = z.object({
  category: VaultDocumentCategorySchema.optional(),
  status: VaultDocumentStatusSchema.optional(),
  sourceType: VaultDocumentSourceSchema.optional(),
  search: z.string().optional(),
  page: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).max(200).optional(),
});

export const VaultDocumentStatsDTOSchema = z.object({
  total: z.number(),
  draft: z.number(),
  final: z.number(),
  byCategory: z.record(z.string(), z.number()),
});
export type VaultDocumentStatsDTO = z.infer<typeof VaultDocumentStatsDTOSchema>;


export interface KeyDocumentTestDTO {
  requirementsUpdatedAt: string;
  fingerprint: string;
  statusId: string;
  title: string;
  taskCode: string | null;
  status: string;
  requirement: 'required' | 'optional';
  coverage: 'full' | 'partial';
  remaining: string | null;
  auditorAsk: string | null;
  instructions: string | null;
  owner: {
    id: string;
    name: string;
    email: string | null;
    image: string | null;
  } | null;
  reviewer: {
    id: string;
    name: string;
    email: string | null;
    image: string | null;
  } | null;
  eligibility: boolean;
  reasons: string[];
}
export interface KeyDocumentRequirementDTO {
  frameworks?: string[];
  editorPath?: string;
  deliverableKey?: string;
  /** Third-party material: offer upload, never an AI draft. */
  uploadOnly?: boolean;
  category: "Governance" | "Security" | "Engineering" | "AI";
  key: string;
  title: string;
  guidance: string;
  requirement: 'required' | 'optional';
  preparation: 'missing' | 'draft' | 'prepared' | 'needs_review';
  tests: KeyDocumentTestDTO[];
  documents: Array<{
    id: string;
    title: string;
    contentHash: string;
    preparedVersionId: string | null;
    preparedAt: Date | null;
    category: VaultDocumentCategory;
    sourceType: VaultDocumentSource;
    status: VaultDocumentStatus;
    linkedTests: Array<{
      statusId: string;
      title: string;
      taskCode: string | null;
    }>;
    isLinked: boolean;
    isPrepared: boolean;
    hasCurrentPreparedVersion?: boolean;
    isSelected?: boolean;
  }>;
}
export interface KeyDocumentSubmissionPreviewDTO {
  fingerprint: string;
  tests: KeyDocumentTestDTO[];
  documents: Array<{ id: string; title: string; contentHash: string; versionId: string }>;
}
